import React, { useEffect } from 'react';

import { Wrapper } from '@/pages/LandingPage/sections/ContactUs/components/ui/Wrapper';
import { useTheme } from '@/services/theme';
import { Content } from '@/pages/LandingPage/sections/ContactUs/components/ui/Content';
import { Title } from '@/pages/LandingPage/sections/ContactUs/components/ui/Title';
import { Form } from '@/pages/LandingPage/sections/ContactUs/components/ui/Form';
import { ContactFormField } from '@/pages/LandingPage/sections/ContactUs/components/ContactFormField';
import { ContactFormSelect } from '@/pages/LandingPage/sections/ContactUs/components/ContactFormSelect';
import { categoryOptions } from '@/pages/LandingPage/sections/ContactUs/constants/categoryOptions';
import { Button } from '@/pages/LandingPage/sections/ContactUs/components/ui/Button';
import { DoneWindow } from '@/pages/LandingPage/sections/ContactUs/components/DoneWindow';
import { FormFieldKey } from '@/pages/LandingPage/sections/ContactUs/types/FormFieldKey';
import { FormFieldValue } from '@/pages/LandingPage/sections/ContactUs/types/FormFieldValue';
import { defaultFormValue } from '@/pages/LandingPage/sections/ContactUs/constants/defaultFormValue';
import { ReactComponent as Loading } from '@/pages/LandingPage/sections/ContactUs/assets/loading.svg';
import { ErrorLabel } from '@/pages/LandingPage/sections/ContactUs/components/ui/ErrorLabel';
import { googleContactUsFormId } from '@/pages/LandingPage/sections/ContactUs/constants/googleContactUsFormId';
import { PrivacyNoticeLink } from '@/pages/LandingPage/sections/ContactUs/components/ui/PrivacyNoticeLink';

export const ContactUs: React.FC = () => {
  const { theme } = useTheme();
  const [formFields, setFormFields] =
    React.useState<Record<FormFieldKey, FormFieldValue>>(defaultFormValue);
  const [loading, setLoading] = React.useState(false);
  const [doneModalIsOpen, setDoneModalIsOpen] = React.useState<boolean>(false);
  const [sendingError, setSendingError] = React.useState<boolean>(false);
  const errorElementRef = React.useRef<HTMLParagraphElement>(null);

  function changeFormField(key: FormFieldKey, value: string | null, error?: string) {
    if (!loading) {
      setFormFields(old => ({
        ...old,
        [key]: { value: value != null ? value : old[key].value, error },
      }));
    }
  }

  function changeCompanyName(value: string) {
    changeFormField('company', value);
  }

  function changeEmail(value: string) {
    changeFormField('email', value);
  }

  function changeCategory(value: string) {
    changeFormField('category', value);
  }

  function changeMessage(value: string) {
    changeFormField('message', value);
  }

  function changeTelegram(value: string) {
    changeFormField('telegram', value);
  }

  async function submitForm(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();

    setLoading(false);
    setSendingError(false);

    let hasError = false;

    if (formFields.company.value.length === 0) {
      changeFormField('company', null, 'Field is required');
      hasError = true;
    }

    if (formFields.email.value.length === 0) {
      changeFormField('email', null, 'Field is required');
      hasError = true;
    } else {
      if (!/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(formFields.email.value)) {
        changeFormField('email', null, 'Invalid email format');
        hasError = true;
      }
    }

    if (formFields.message.value.length === 0) {
      changeFormField('message', null, 'Field is required');
      hasError = true;
    }

    if (formFields.category.value.length === 0) {
      changeFormField('category', null, 'Field is required');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    function scrollToSendingError() {
      if (errorElementRef.current) {
        errorElementRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }

    const formData = new FormData();
    formData.set('entry.1961450729', formFields.company.value);
    formData.set('entry.523874110', formFields.email.value);
    formData.set('entry.2087883900', formFields.message.value);
    formData.set('entry.2071946094', formFields.category.value);
    formData.set('entry.680759430', formFields.telegram.value || '');

    setLoading(true);

    const response = await fetch(
      `https://docs.google.com/forms/d/e/${googleContactUsFormId}/formResponse`,
      {
        method: 'POST',
        mode: 'no-cors',
        body: formData,
      },
    );

    if (response.type === 'opaque') {
      setDoneModalIsOpen(true);
    } else {
      setSendingError(true);
      scrollToSendingError();
    }
    setLoading(false);
  }

  useEffect(() => {
    setFormFields(defaultFormValue);
  }, [doneModalIsOpen]);

  useEffect(() => {
    setSendingError(false);
  }, [formFields]);

  return (
    <Wrapper theme={theme}>
      <Content>
        <Title theme={theme}>Contact Us</Title>
        <Form theme={theme}>
          <ContactFormField
            value={formFields.company.value}
            error={formFields.company.error}
            onChange={changeCompanyName}
            name="company_name"
            type="text"
            label="Company name"
            placeholder="Company name"
            isRequired
          />
          <ContactFormField
            value={formFields.email.value}
            error={formFields.email.error}
            onChange={changeEmail}
            name="user_email"
            type="email"
            label="Your e-mail"
            placeholder="email@example.com"
            isRequired
          />
          <ContactFormSelect
            label="Select your category"
            type="select"
            options={categoryOptions}
            placeholder="Select"
            value={formFields.category.value}
            error={formFields.category.error}
            onChange={changeCategory}
          />
          <ContactFormField
            value={formFields.message.value}
            error={formFields.message.error}
            onChange={changeMessage}
            name="message"
            type="textarea"
            label="Message"
            labelSuffix="Try to keep it to around 100 words"
            placeholder="Message"
            isRequired
          />
          <ContactFormField
            value={formFields.telegram.value}
            error={formFields.telegram.error}
            onChange={changeTelegram}
            name="telegram"
            type="text"
            label="Telegram handle"
            placeholder="@..."
          />
          <Button theme={theme} onClick={submitForm} disabled={loading}>
            {loading ? <Loading /> : 'Send'}
          </Button>
        </Form>
        {sendingError && (
          <ErrorLabel ref={errorElementRef} theme={theme} align="center">
            Something went wrong, please try again.
          </ErrorLabel>
        )}
        <p>
          <PrivacyNoticeLink theme={theme} href="/Mellow-Privacy-Notice.pdf" target="_blank">
            Privacy Notice
          </PrivacyNoticeLink>
        </p>
      </Content>
      <DoneWindow isOpen={doneModalIsOpen} onClose={() => setDoneModalIsOpen(false)} />
    </Wrapper>
  );
};
